import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineChatAlt2,
  HiOutlineX,
  HiOutlineSupport,
  HiOutlineArrowSmRight,
  HiOutlineShieldCheck,
  HiOutlineCube,
  HiOutlineTruck,
  HiOutlineQuestionMarkCircle,
  HiOutlineExternalLink,
} from "react-icons/hi";
import axios from "utils/axiosConfig";
import { useToast } from "./Toast";
import uri from "../features/config";

const SupportChat = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([
    {
      role: "agent",
      content:
        "Sentinel Alpha online. Digital support node ready for tactical assistance. How can I help you, Player?",
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    },
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [quickActions] = useState([
    {
      label: "Shipping Info",
      query: "What's your shipping policy?",
      icon: <HiOutlineTruck size={14} />,
    },
    {
      label: "Track Order",
      query: "How can I track my order?",
      icon: <HiOutlineCube size={14} />,
    },
    {
      label: "Returns",
      query: "What's your return policy?",
      icon: <HiOutlineQuestionMarkCircle size={14} />,
    },
  ]);
  const scrollRef = useRef(null);

  // Helper function to render message content with product links
  const renderMessageContent = (content, products = []) => {
    // If there are products in the message, parse and render with buttons
    if (products && products.length > 0) {
      return (
        <div>
          <p className="mb-3">{content.split("\n\n")[0]}</p>
          <div className="space-y-2 mt-3">
            {products.map((product, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setIsOpen(false);
                  navigate(`/product/${product.code}`);
                }}
                className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-cyan-500/20 border border-white/10 hover:border-cyan-500/30 rounded-xl transition-all group text-left"
              >
                <div className="flex-1">
                  <p className="text-[10px] font-black text-white group-hover:text-cyan-300">
                    {product.name}
                  </p>
                  <p className="text-[8px] text-gray-400 mt-0.5">
                    ${product.price?.toFixed(2)} •{" "}
                    {product.stock > 0 ? "🟢 In Stock" : "🔴 Out of Stock"}
                  </p>
                </div>
                <HiOutlineExternalLink
                  className="text-gray-500 group-hover:text-cyan-400 ml-2"
                  size={14}
                />
              </button>
            ))}
          </div>
          <p className="text-[9px] text-gray-500 mt-3 italic">
            Click a product to view details!
          </p>
        </div>
      );
    }

    // Parse message for product codes in format "Code: XXX" and make them clickable
    const codePattern = /Code:\s*([A-Z0-9-]+)/gi;
    const parts = content.split(codePattern);

    if (parts.length > 1) {
      return (
        <div>
          {parts.map((part, idx) => {
            // Every odd index is a product code (captured group)
            if (idx % 2 === 1) {
              return (
                <button
                  key={idx}
                  onClick={() => {
                    setIsOpen(false);
                    navigate(`/product/${part}`);
                  }}
                  className="inline-flex items-center gap-1 px-2 py-0.5 mx-1 bg-cyan-500/20 hover:bg-cyan-500/40 border border-cyan-500/30 rounded-lg text-cyan-300 hover:text-white transition-all text-[9px] font-bold"
                >
                  {part}
                  <HiOutlineExternalLink size={10} />
                </button>
              );
            }
            return <span key={idx}>{part}</span>;
          })}
        </div>
      );
    }

    return content;
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const processQuery = async (query) => {
    try {
      // Try the enhanced support query endpoint first
      const response = await axios.post(
        `${uri}/api/agents/support/query`,
        {
          query,
          context: { chatHistory: chatHistory.slice(-5) },
        },
        {
          headers: {
            "x-agent-id": "agent_sentinel_alpha",
            "x-agent-key": "GH_SENTINEL_ALPHA_SECURE_NODE",
          },
        }
      );

      const data = response.data.data;

      return {
        content: data.content,
        type: data.type,
        confidence: data.confidence,
        products: data.products || [], // Pass products for button rendering
      };
    } catch (error) {
      // Fallback to basic intelligence endpoint
      try {
        const response = await axios.get(`${uri}/api/agents/intelligence`, {
          headers: {
            "x-agent-id": "agent_sentinel_alpha",
            "x-agent-key": "GH_SENTINEL_ALPHA_SECURE_NODE",
          },
        });

        const intelligence = response.data.data;
        const lowerQuery = query.toLowerCase();

        // Basic keyword matching fallback
        if (lowerQuery.includes("shipping")) {
          return {
            content: intelligence.shippingInfo.policy,
            type: "shipping",
          };
        } else if (
          lowerQuery.includes("stock") ||
          lowerQuery.includes("available")
        ) {
          const inStock = intelligence.products.filter(
            (p) => (p.stock || 0) > 0
          );
          return {
            content: `Scanning Arsenal... ${inStock.length} products currently in stock and ready for deployment.`,
            type: "inventory",
          };
        }

        return {
          content:
            "Processing query... Please provide more details for accurate tactical response.",
          type: "general",
        };
      } catch (fallbackError) {
        throw new Error("Intelligence node offline");
      }
    }
  };

  const handleSend = async (e, quickQuery = null) => {
    if (e) e.preventDefault();
    const queryText = quickQuery || message;
    if (!queryText.trim()) return;

    const userMessage = {
      role: "user",
      content: queryText,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    setChatHistory((prev) => [...prev, userMessage]);
    setMessage("");
    setIsTyping(true);

    try {
      const response = await processQuery(queryText);

      const agentMessage = {
        role: "agent",
        content: response.content,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        type: response.type,
        confidence: response.confidence,
        products: response.products, // Include products for button rendering
      };

      // Simulate typing delay based on response length
      await new Promise((resolve) =>
        setTimeout(resolve, Math.min(response.content.length * 10, 1500))
      );

      setChatHistory((prev) => [...prev, agentMessage]);
    } catch (error) {
      toast.error("Transmission Interrupted: Intelligence Node Offline");
      setChatHistory((prev) => [
        ...prev,
        {
          role: "agent",
          content:
            "Signal disruption detected. Unable to process query. Please try again.",
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
          isError: true,
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleQuickAction = (query) => {
    handleSend(null, query);
  };

  return (
    <>
      {/* Floating Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[99999] p-4 sm:p-5 bg-gradient-to-br from-cyan-500 to-cyan-600 text-black rounded-full shadow-[0_0_30px_rgba(0,242,255,0.5)] hover:shadow-[0_0_40px_rgba(0,242,255,0.7)] transition-all transform hover:scale-110 active:scale-95 group"
          style={{ position: "fixed" }}
        >
          <HiOutlineChatAlt2
            size={26}
            className="group-hover:rotate-12 transition-transform"
          />
          <span className="absolute -top-1 -right-1 flex h-5 w-5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
            <span className="relative inline-flex rounded-full h-5 w-5 bg-white border-2 border-cyan-500"></span>
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[99999] w-[calc(100vw-32px)] sm:w-[350px] md:w-[400px] max-w-[400px] h-[calc(100vh-100px)] sm:h-[600px] max-h-[600px] bg-[#0b0e14]/95 backdrop-blur-2xl border border-white/10 rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden animate-slideInRight">
          {/* Header */}
          <div className="p-6 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-cyan-500/10 rounded-2xl border border-cyan-500/20 text-cyan-400 relative">
                <HiOutlineSupport size={20} />
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-[#0b0e14] animate-pulse"></div>
              </div>
              <div>
                <h3 className="text-white font-black italic tracking-widest uppercase text-xs">
                  Sentinel Alpha
                </h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <div className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse shadow-[0_0_5px_#22c55e]"></div>
                  <span className="text-[8px] text-gray-500 font-bold uppercase tracking-widest">
                    Neural_Link: Active
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-white/5 rounded-xl transition-colors text-gray-500 hover:text-white"
            >
              <HiOutlineX size={20} />
            </button>
          </div>

          {/* Quick Actions */}
          <div className="px-4 py-3 border-b border-white/5 bg-white/[0.02] flex gap-2 overflow-x-auto scrollbar-hide">
            {quickActions.map((action, i) => (
              <button
                key={i}
                onClick={() => handleQuickAction(action.query)}
                disabled={isTyping}
                className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-cyan-500/10 border border-white/10 hover:border-cyan-500/20 rounded-xl text-[9px] font-bold text-gray-400 hover:text-cyan-400 transition-all whitespace-nowrap disabled:opacity-50"
              >
                {action.icon}
                {action.label}
              </button>
            ))}
          </div>

          {/* Messages Area */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar"
          >
            {chatHistory.map((msg, i) => (
              <div
                key={i}
                className={`flex flex-col ${
                  msg.role === "user" ? "items-end" : "items-start"
                }`}
              >
                <div
                  className={`max-w-[85%] p-4 rounded-2xl text-[11px] font-bold leading-relaxed whitespace-pre-line ${
                    msg.role === "user"
                      ? "bg-white/5 border border-white/10 text-white rounded-tr-none"
                      : msg.isError
                      ? "bg-red-500/10 border border-red-500/20 text-red-300 rounded-tl-none"
                      : "bg-cyan-500/10 border border-cyan-500/10 text-cyan-100 rounded-tl-none italic"
                  }`}
                >
                  {msg.role === "user"
                    ? msg.content
                    : renderMessageContent(msg.content, msg.products)}
                </div>
                <div className="flex items-center gap-2 mt-2 px-1">
                  <span className="text-[8px] text-gray-600 font-black tracking-widest uppercase">
                    {msg.timestamp}
                  </span>
                  {msg.confidence && (
                    <span
                      className={`text-[7px] px-1.5 py-0.5 rounded-full font-bold uppercase ${
                        msg.confidence > 0.8
                          ? "bg-green-500/10 text-green-400"
                          : msg.confidence > 0.5
                          ? "bg-amber-500/10 text-amber-400"
                          : "bg-gray-500/10 text-gray-400"
                      }`}
                    >
                      {Math.round(msg.confidence * 100)}% conf
                    </span>
                  )}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex items-center gap-2 text-cyan-500/50">
                <div className="flex gap-1">
                  <span className="w-1 h-1 bg-current rounded-full animate-bounce"></span>
                  <span className="w-1 h-1 bg-current rounded-full animate-bounce [animation-delay:0.2s]"></span>
                  <span className="w-1 h-1 bg-current rounded-full animate-bounce [animation-delay:0.4s]"></span>
                </div>
                <span className="text-[8px] font-black uppercase tracking-widest italic">
                  Processing Neural Query...
                </span>
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="p-6 border-t border-white/10 bg-white/[0.02]">
            <form onSubmit={handleSend} className="relative flex items-center">
              <input
                type="text"
                placeholder="Request tactical assistance..."
                className="w-full pl-6 pr-14 py-4 bg-white/5 border border-white/10 rounded-2xl text-base md:text-[11px] font-black italic text-white placeholder:text-gray-700 focus:border-cyan-500/50 transition-all outline-none"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isTyping}
              />
              <button
                type="submit"
                disabled={isTyping || !message.trim()}
                className="absolute right-2 p-3 bg-cyan-500 text-black rounded-xl hover:bg-cyan-400 transition-all transform active:scale-90 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <HiOutlineArrowSmRight size={20} />
              </button>
            </form>
            <div className="mt-4 flex items-center justify-center gap-2 opacity-30">
              <HiOutlineShieldCheck size={12} className="text-gray-500" />
              <p className="text-[8px] text-gray-600 font-bold uppercase tracking-[0.2em]">
                Secured Neural Protocol v.3.0
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportChat;
